import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiPlus, FiTrash2, FiX } from 'react-icons/fi';
import AdsMediaUploadSection from './AdsMediaUploadSection';

const inputClassName = 'w-full px-3 py-2 border border-gray-300 rounded-md text-blue-700 font-bold';
const emptySource = { source: '', link: '' };

const AdsModal = ({ mode = 'create', ad = null, onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        adsCategory: '',
        adsImage: [],
        adsGif: [],
        adsVideos: [],
        adsSource: [{ ...emptySource }]
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (mode === 'edit' && ad) {
            setFormData({
                adsCategory: ad.adsCategory || '',
                adsImage: ad.adsImage?.length ? [...ad.adsImage] : [],
                adsGif: ad.adsGif?.length ? [...ad.adsGif] : [],
                adsVideos: ad.adsVideos?.length ? [...ad.adsVideos] : [],
                adsSource: ad.adsSource?.length
                    ? ad.adsSource.map((row) => ({
                        source: row.source || '',
                        link: row.link || ''
                    }))
                    : [{ ...emptySource }]
            });
        }
    }, [mode, ad]);

    const updateSourceField = (index, field, value) => {
        const next = [...formData.adsSource];
        next[index] = { ...(next[index] || emptySource), [field]: value };
        setFormData((prev) => ({ ...prev, adsSource: next }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const payload = {
                adsCategory: formData.adsCategory.trim(),
                adsImage: formData.adsImage,
                adsGif: formData.adsGif,
                adsVideos: formData.adsVideos,
                adsSource: formData.adsSource
                    .map((item) => ({
                        source: item.source?.trim() || '',
                        link: item.link?.trim() || ''
                    }))
                    .filter((item) => item.source || item.link)
            };

            const token = localStorage.getItem('token');
            const headers = {
                Authorization: `Bearer ${token}`,
                'ngrok-skip-browser-warning': 'true'
            };

            const request = mode === 'edit'
                ? axios.put(`${process.env.REACT_APP_API_URL}/api/ads/${ad._id}`, payload, { headers })
                : axios.post(`${process.env.REACT_APP_API_URL}/api/ads`, payload, { headers });

            const response = await request;
            if (response.data.success) {
                onSuccess(response.data.data);
            }
        } catch (err) {
            setError(`Failed to ${mode} ad: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const adId = mode === 'edit' && ad?._id ? ad._id : null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 w-full max-w-5xl my-8 min-w-0 overflow-x-hidden">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">
                        {mode === 'edit' ? `Edit Ad #${ad?.adsID ?? ''}` : 'Create Ad'}
                    </h2>
                    <button onClick={onCancel} className="text-gray-500 hover:text-gray-700" aria-label="Close modal">
                        <FiX className="w-6 h-6" />
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                            <input
                                type="text"
                                value={formData.adsCategory}
                                onChange={(e) => setFormData((prev) => ({ ...prev, adsCategory: e.target.value }))}
                                className={inputClassName}
                                placeholder="Game, Vape, etc"
                            />
                        </div>

                        <AdsMediaUploadSection
                            label="Images"
                            mediaType="adsImage"
                            urls={formData.adsImage}
                            onUrlsChange={(urls) => setFormData((prev) => ({ ...prev, adsImage: urls }))}
                            adId={adId}
                        />
                        <AdsMediaUploadSection
                            label="GIFs"
                            mediaType="adsGif"
                            urls={formData.adsGif}
                            onUrlsChange={(urls) => setFormData((prev) => ({ ...prev, adsGif: urls }))}
                            adId={adId}
                        />
                        <AdsMediaUploadSection
                            label="Videos"
                            mediaType="adsVideos"
                            urls={formData.adsVideos}
                            onUrlsChange={(urls) => setFormData((prev) => ({ ...prev, adsVideos: urls }))}
                            adId={adId}
                        />

                        <div className="border border-gray-200 rounded-lg p-4">
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="font-medium text-gray-800">Sources & Links</h3>
                                <button
                                    type="button"
                                    onClick={() => setFormData((prev) => ({
                                        ...prev,
                                        adsSource: [...prev.adsSource, { ...emptySource }]
                                    }))}
                                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                >
                                    <FiPlus className="mr-1" /> Add
                                </button>
                            </div>
                            <div className="space-y-3">
                                {formData.adsSource.map((row, index) => (
                                    <div key={`source-${index}`} className="grid grid-cols-1 md:grid-cols-2 gap-2 items-start">
                                        <input
                                            type="text"
                                            value={row.source}
                                            onChange={(e) => updateSourceField(index, 'source', e.target.value)}
                                            placeholder="Source"
                                            className={inputClassName}
                                        />
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={row.link}
                                                onChange={(e) => updateSourceField(index, 'link', e.target.value)}
                                                placeholder="Link URL"
                                                className={inputClassName}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const next = formData.adsSource.filter((_, i) => i !== index);
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        adsSource: next.length ? next : [{ ...emptySource }]
                                                    }));
                                                }}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                                                aria-label="Remove source row"
                                            >
                                                <FiTrash2 />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                            {loading ? 'Saving...' : mode === 'edit' ? 'Update Ad' : 'Create Ad'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdsModal;
